// `$` is xOpat's i18n namespace (`$.t` / `$.i18n`), NOT jQuery — jQuery is no
// longer shipped. The UI bundle is emitted BEFORE the core scripts, so the
// placeholder is installed here too; whichever runs first wins, and `src/app.ts`
// later swaps `t` for the real i18next translator. Idempotent by design — see
// `src/classes/app/i18n-dom.ts` and AGENTS.md §3.
if (!globalThis.$) globalThis.$ = {};
if (typeof globalThis.$.t !== "function") {
    globalThis.$.t = (key) => String(key).split(".").findLast(Boolean);
}

globalThis.UI = {};
globalThis.VANCOMPONENTS = {};

import van from "./vanjs.mjs";

import { BaseComponent } from "./classes/baseComponent.mjs";

// ELEMENTS
import { Button } from "./classes/elements/buttons.mjs";
import { FAIcon } from "./classes/elements/fa-icon.mjs";
import { PhIcon } from "./classes/elements/ph-icon.mjs";
import { ImageIcon } from "./classes/elements/image-icon.mjs";
import { Join } from "./classes/elements/join.mjs";
import { Div } from "./classes/elements/div.mjs";
import { Dropdown } from "./classes/elements/dropdown.mjs";
import { Checkbox } from "./classes/elements/checkbox.mjs";
import { Select } from "./classes/elements/select.mjs";
import { RawHtml } from "./classes/elements/rawHtml.mjs";
import { Alert } from "./classes/elements/alert.mjs";
import { StretchGrid } from "./classes/elements/stretchGrid.mjs";
import { Input } from "./classes/elements/input.mjs";
import { Badge } from "./classes/elements/badge.mjs";
import { Title } from "./classes/elements/title.mjs";
import { Collapse } from "./classes/elements/collapse.mjs";
import { Loading } from "./classes/elements/loading.mjs";

// COMPONENTS
import { Menu } from "./classes/components/menu.mjs";
import { MainPanel } from "./classes/components/mainPanel.mjs";
import { MultiPanelMenuTab } from "./classes/components/multiPanelMenuTab.mjs";
import { MultiPanelMenu } from "./classes/components/multiPanelMenu.mjs";
import { FullscreenMenu } from "./classes/components/fullscreenMenu.mjs";
import { FullscreenMenuNavTab } from "./classes/components/fullscreenMenuNavTab.mjs";
import { FullscreenMenuPanel } from "./classes/components/fullscreenMenuPanel.mjs";
import { TabsMenu } from "./classes/components/tabsMenu.mjs";
import { Toolbar } from "./classes/components/toolbar/toolbar.mjs";
import { ToolbarItem} from "./classes/components/toolbar/toolbarItem.mjs";
import { ToolbarSeparator } from "./classes/components/toolbar/toolbarSeparator.mjs";
import { ToolbarGroup } from "./classes/components/toolbar/toolbarGroup.mjs";
import { ToolbarChoiceGroup } from "./classes/components/toolbar/toolbarChoiceGroup.mjs";
import { ToolbarPanelButton } from "./classes/components/toolbar/toolbarPanelButton.mjs";
import { ShaderLayer } from "./classes/components/shaderLayer.mjs";
import { ShaderSideMenu } from "./classes/components/shaderSideMenu.mjs";
import { FloatingWindow } from "./classes/components/floatingWindow.mjs";
import { MainLayout } from "./classes/components/mainLayout.mjs";
import { Toast } from "./classes/components/toast.mjs";
import { MenuTabBanner } from "./classes/components/menuTabBanner.mjs";
import { RightSideViewerMenu } from "./classes/components/rightSideViewerMenu.mjs";
import { NavigatorSideMenu } from "./classes/components/navigatorSideMenu.mjs";
import { Explorer } from "./classes/components/explorer.mjs";
import { DockableWindow } from "./classes/components/dockableWindow.mjs";
import { StatusBar } from "./classes/components/statusBar.mjs";
import { Modal } from "./classes/components/modal.mjs";
import { IllustratedModal } from "./classes/components/illustratedModal.mjs";
import { TutorialsModal } from "./classes/components/tutorialsModal.mjs";
import { LoginModal } from "./classes/components/loginModal.mjs";
import { ProgressDialog } from "./classes/components/progressDialog.mjs";
import { TagSelect } from "./classes/components/tagSelect.mjs";
import { Autocomplete } from "./classes/components/autocomplete.mjs";
import { ContextMenu } from "./classes/components/contextMenu.mjs";
import { KeymapPanel } from "./classes/components/keymapPanel.mjs";
import { RenderDebugPanel } from "./classes/components/renderDebugPanel.mjs";
import { UserRolesPanel } from "./classes/components/userRolesPanel.mjs";
import { SuggestionEditor } from "./classes/components/suggestionEditor.mjs";

// SERVICES
import { GlobalTooltip } from "./services/globalTooltip.mjs";
import { AppBar } from "./services/appBar.mjs";
import { MobileBottomBar } from "./services/mobileBottomBar.mjs";
import { FloatingManager } from "./services/floatingManager.mjs";
import { FullscreenMenus } from "./services/fullscreenMenus.mjs";

// MIXINS
import { VisibilityManager } from "./classes/mixins/visibilityManager.mjs";
import * as utils from "./classes/mixins/utils.mjs";

class ServiceContainer {
    _globalTooltip = null;
    _appBar = null;
    _MobileBottomBar = null;
    _floatingManager = null;
    _fullscreenMenus = null;

    get GlobalTooltip() {
        if (!this._globalTooltip) {
            this._globalTooltip = new GlobalTooltip();
        }
        return this._globalTooltip;
    }

    get AppBar() {
        if (!this._appBar) {
            this._appBar = new AppBar();
        }
        return this._appBar;
    }

    get MobileBottomBar() {
        if (!this._MobileBottomBar) {
            this._MobileBottomBar = new MobileBottomBar();
        }
        return this._MobileBottomBar;
    }

    get FloatingManager() {
        if (!this._floatingManager) {
            this._floatingManager = new FloatingManager();
        }
        return this._floatingManager;
    }

    get FullscreenMenus() {
        if (!this._fullscreenMenus) {
            this._fullscreenMenus = new FullscreenMenus();
        }
        return this._fullscreenMenus;
    }
}

const UI = {
    BaseComponent,
    Button, FAIcon, PhIcon, ImageIcon, Join, Div, Dropdown, Checkbox, Select, RawHtml, Alert,
    StretchGrid, Input, Badge, Title, Collapse, Loading,

    Menu, MainPanel, MultiPanelMenuTab, MultiPanelMenu, FullscreenMenu, FullscreenMenuNavTab,
    FullscreenMenuPanel, TabsMenu, ShaderLayer, ShaderSideMenu, FloatingWindow, MainLayout,
    Toast, MenuTabBanner, RightSideViewerMenu, NavigatorSideMenu, Explorer, Toolbar, ToolbarItem,
    ToolbarSeparator, ToolbarGroup, ToolbarChoiceGroup, ToolbarPanelButton, DockableWindow, StatusBar,
    Modal, IllustratedModal, TutorialsModal, LoginModal, ProgressDialog, TagSelect, Autocomplete,
    ContextMenu, KeymapPanel, RenderDebugPanel, SuggestionEditor, UserRolesPanel,

    Services: new ServiceContainer(),
    Mixins: {
        VisibilityManager,
        Utilities: utils
    }
};

globalThis.UI = UI;

globalThis.vanRegister = function (id, component) {
    globalThis.VANCOMPONENTS[id] = component;
}

globalThis.van = van;
